<script setup lang="ts">
import { infiniteScrollingHelper } from '../lib/infiniscroll'
import { RateLimit, Shuffler } from '~~/shared/lib/pluralistic'
import type { Post } from '~~/prisma/generated/client'
const { Settings } = useSettings()

const shuffler = new Shuffler(
    // on average, one post every five can be from a source. But burst of 3 posts
    // in a row can happen.
    new RateLimit<number>(
      Settings.value.pluralisticInitial,
      Settings.value.pluralisticIncrement,
      Settings.value.pluralisticLimit,
      Settings.value.pluralisticCost,
    ),
    // keep a reserve of posts which are not displayed, waiting for next batch
    8)


const props = defineProps(
  {
    api: { type: String, required: true },
    pluralistic: { type: Boolean, default: false },
    offsetPagination: { type: Boolean, default: false }
  })

const initialQueryParams = new URLSearchParams()
// this only makes sense for /api/post/ID (get post by blob); and even then,
// it will only apply pluralism to blobs which are configured to support it.
// Always setting it is just easier than trying to be smart about it.
initialQueryParams.set("applyPluralism", "1")
const { data: initialPosts = [] } = await useFetch<Post[]>(`${props.api}?${initialQueryParams}`)
const posts = ref([...(unref(initialPosts) ?? [])])

function onPostUpdated(updatedPost: Post & { tags: string[] }) {
  const index = unref(posts).findIndex( (p: Post) => p.id === updatedPost.id)
  const post = posts.value[index] as Post
  post.title = updatedPost.title
  post.summary = updatedPost.summary
  post.tags = updatedPost.tags
  post.visible = updatedPost.visible
  post.pinned = updatedPost.pinned
}

function onPostRemoved(post: Post) {
  const index = posts.value.findIndex((p: Post) => p.id === post.id)
  console.log("remove", index)
  if (index != -1) {
    posts.value.splice(index, 1)
  }
}

const loading = ref(false)
const shouldFetchAgain = ref<boolean>(true)
const infiniteScrolling = async ({isIntersecting} : {isIntersecting: boolean}) => {
  if(!isIntersecting) {  // we don't care unless we're at the end
    return
  }
  if(!shouldFetchAgain.value) { // this means we've already hit the end
    return
  }
  loading.value = true
  const foundSomething = await infiniteScrollingHelper(
    posts, props.api, props.offsetPagination ? "offset" : "cursor",
    props.pluralistic, shuffler)
  shouldFetchAgain.value = foundSomething
  loading.value = false
}
</script>

<template>
  <PostList
    :posts="posts"
    :loading="loading"
    @post-updated="onPostUpdated"
    @post-removed="onPostRemoved"
    @loadMore="infiniteScrolling"
  />
</template>
